<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="item-actions">
				<v-btn
					v-if="canManageSubItemCategories"
					depressed
					outlined
					@click="duplicateSubItem()"
					:disabled="duplicateActive || subItemData.subItemCategoryId == null"
					>Duplicate</v-btn
				>
				<v-btn
					v-if="canManageSubItemCategories"
					depressed
					outlined
					@click="deleteWarning = !deleteWarning"
					:disabled="subItemData.subItemCategoryId == null"
					>Delete</v-btn
				>
				<v-btn depressed outlined @click="$router.push('/inventory/sub-item-category')"
					>Cancel</v-btn
				>
				<v-btn v-if="canManageSubItemCategories" depressed color="success" @click="saveSubItem()"
					>Save</v-btn
				>
			</div>
		</v-col>

		<v-col cols="6" class="flex-auto mx-auto">
			<div class="item-form">
				<div class="form-input">
					<label>Sub-Item Category Name</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="subItemData.name"
						:disabled="!canManageSubItemCategories"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="subItemData.description"
						:disabled="!canManageSubItemCategories"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>System Category</label>
					<v-select
						v-model="subItemData.systemCategoryId"
						:items="$systemCategories.subItemCategories"
						item-text="text"
						item-value="systemCategoryId"
						dense
						outlined
						hide-details="auto"
						@change="searchResults = []"
						:disabled="disabled"
					></v-select>
				</div>
			</div>

			<div class="item-sizes">
				<div class="item-sizes-description">
					<p><em>Deleting a package will NOT affect any past orders</em></p>
				</div>
				<div class="item-sizes-table">
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Available Items</div>
							<div class="flex-table-cell" role="column-header">Description</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
						</div>
					</div>
					<div class="flex-table" role="table-body">
						<div
							v-for="(data, i) in subItemData.subItems"
							:key="i"
							class="flex-table-row"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">{{ i + 1 }}</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-combobox
										prepend-inner-icon="mdi-magnify"
										dense
										outlined
										placeholder="Search Style Codes"
										hide-details="auto"
										v-model="subItemData.subItems[i]"
										:items="searchResults"
										item-text="styleCode"
										:disabled="!subItemData.systemCategoryId || !canManageSubItemCategories"
										@keyup="searchInventoryItems($event.srcElement.value)"
									>
										<template v-slot:item="{ item, attrs, on }">
											<v-list-item v-on="on" v-bind="attrs">
												<v-list-item-content>
													<v-list-item-title>
														<v-row no-gutters align="center">
															<SystemCategoryIcon :systemCategoryId="item.systemCategoryId" />
															{{ `${item.styleCode} - ${item.description}` }}
														</v-row>
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-combobox>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										:value="data?.description"
										disabled
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									:disabled="!canManageSubItemCategories"
									icon
									color="primary"
									@click="removeSubItemRow(i)"
									><v-icon style="font-size: 2rem">mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</div>
				</div>
				<div class="item-sizes-actions">
					<v-btn v-if="canManageSubItemCategories" text @click="addSubItemRow()"
						><v-icon>mdi-plus</v-icon> Add a Row</v-btn
					>
				</div>
			</div>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete this sub-item category?'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteSubItem();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import systemCategories from "../../helpers/systemCategories";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";
export default {
	name: "SubItemCategoryView",
	props: {
		subItemCategoryId: {
			type: Number,
			default: null,
		},
	},
	components: {
		WarningModal,
		SystemCategoryIcon,
	},
	data() {
		return {
			subItemData: {
				subItemCategoryId: null,
				systemCategoryId: null,
				name: "",
				description: "",
				subItems: [
					{
						inventoryItemId: 0,
						styleCode: "",
						description: "",
					},
				],
			},
			systemCategories: systemCategories.subItemCategories,
			searchResults: [],
			duplicateActive: false,
			deleteWarning: false,
		};
	},
	computed: {
		canManageSubItemCategories() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Inventory_SubItemCategory_Create_Update_Delete) ||
				user.isAdmin
			);
		},
		disabled() {
			return this.subItemData.subItems.filter((el) => el?.styleCode).length > 0;
		},
	},
	methods: {
		addSubItemRow() {
			let newItem = {
				inventoryItemId: null,
				styleCode: "",
				description: "",
			};

			this.subItemData.subItems.push(newItem);
		},

		removeSubItemRow(i) {
			this.subItemData.subItems.splice(i, 1);
		},

		duplicateSubItem() {
			//remove the subitem id so we can save a new copy and replace the name
			this.subItemData.subItemCategoryId = null;
			this.subItemData.name = `${this.subItemData.name} (DUPLICATE)`;

			//set duplcate state
			this.duplicateActive = true;
		},

		async deleteSubItem() {
			try {
				const res = await this.$http.delete(
					getApiUrl(`/api/settings/subItemCategory/${this.subItemData.subItemCategoryId}`)
				);
				console.log(res);

				this.$router.push("/inventory/sub-item-category");
			} catch (error) {
				console.log(error);
			}
		},

		async saveSubItem() {
			try {
				//create post/put request to add/update sub category to api
				let res;
				let subCategory = {
					phantonSubItemId: this.subItemData.subItemCategoryId,
					systemCategoryId: this.subItemData.systemCategoryId,
					name: this.subItemData.name,
					description: this.subItemData.description,
					inventoryItemIds: this.subItemData.subItems.map((obj) => obj.inventoryItemId),
				};
				if (this.subItemData.subItemCategoryId) {
					res = await this.$http.put(
						getApiUrl("/api/settings/subItemCategory"),
						subCategory
					);
				} else {
					res = await this.$http.post(
						getApiUrl("/api/settings/subItemCategory"),
						subCategory
					);
				}
				console.log("saving sub category", res);

				this.$router.push("/inventory/sub-item-category");
			} catch (error) {
				console.log(error);
			}
		},

		async searchInventoryItems(searchTerm) {
			try {
				//send gql query to get current inventory items related to searchTerm
				const res = await this.$apollo.query({
					query: gql`
						query inventoryListings($searchTerm: String) {
							inventoryListings(searchTerm: $searchTerm, skip: 0, take: 500, order: { styleCode: ASC }, where: { systemCategoryId: { eq: ${this.subItemData.systemCategoryId} } }) {
								items {
									inventoryItemId
									systemCategoryId
									styleCode
									description
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables: {
						searchTerm,
					},
					fetchPolicy: "no-cache",
				});

				console.log(`searching for packages with ${searchTerm}`, res.data.inventoryListings.items);
				this.searchResults = res.data.inventoryListings.items;
			} catch (error) {
				console.log(error);
			}
		},

		async getSubItemById() {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
				        subItemCategoryById(id: ${this.subItemCategoryId}) {
				            subItemCategoryId
                            name
                            description
                            systemCategoryId
                            subItems {
                                inventoryItemId
                                styleCode
                                description
                            }         
				        }
				    }`,
					fetchPolicy: "no-cache",
				});

				console.log("getting subItems", res);
				this.subItemData = res.data.subItemCategoryById;
			} catch (error) {
				console.log(error);
			}
		},
	},
	async mounted() {
		if (this.subItemCategoryId) {
			await this.getSubItemById();
		}
	},
};
</script>

<style lang="scss" scoped>
.item {
	&-actions {
		display: flex;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0.5rem;
		}
	}

	&-sizes {
		display: flex;
		flex-flow: column nowrap;
		height: 100%;
		margin-top: 2rem;

		&-description {
			p {
				color: $color-gray;
				font-size: 1.4rem;
			}
		}

		&-table {
			flex-grow: 1;
			background-color: $color-white;

			.flex-table-cell {
				.form-input {
					width: 100%;
				}

				&:nth-child(1) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(2) {
					width: 42%;
				}
				&:nth-child(3) {
					width: 42%;
				}
				&:nth-child(4) {
					width: 8%;
					justify-content: center;
				}
			}
		}

		&-actions {
			margin-top: 1rem;

			.v-btn {
				font-size: 1.4rem;
				padding-left: 1rem;

				.v-icon {
					font-size: 2rem;
					margin-right: 0.5rem;
				}
			}
		}
	}
}
</style>
